import {AfterViewInit, Component, inject} from '@angular/core';
import {Location, NgClass, NgIf, TitleCasePipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {FormBuilder, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatSlider, MatSliderThumb} from "@angular/material/slider";
import {barChartData, config} from './stack-chart.config';
import {MatOption, MatSelect} from "@angular/material/select";
import {MatButtonToggle, MatButtonToggleGroup} from "@angular/material/button-toggle";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatProgressBar} from "@angular/material/progress-bar";
import {ModalComponent} from "../../../components/modal/modal.component";
import {ProductsService} from "@services/products/products.service";
import {ActivatedRoute, Router} from "@angular/router";
import {BarChartComponent} from '../../../components/bar-chart/bar-chart.component';
import {ReportService} from '@services/report/report.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ReportStatus} from '@interfaces/report';
import {finalize} from 'rxjs';
import {skin_index} from '@services/products/skin_index';
import {LoginService} from '@services/login/login.service';

@Component({
  selector: 'app-patient-report-detail',
  imports: [
    MatIcon,
    ReactiveFormsModule,
    TitleCasePipe,
    MatSlider,
    MatSliderThumb,
    MatSelect,
    MatOption,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatButton,
    MatProgressBar,
    ModalComponent,
    MatIconButton,
    NgIf,
    NgClass,
    BarChartComponent
  ],
  templateUrl: './patient-report-detail.component.html',
  standalone: true,
  styleUrl: './patient-report-detail.component.scss'
})
export class PatientReportDetailComponent implements AfterViewInit {

  private productsService = inject(ProductsService);
  private loginService = inject(LoginService);
  private _snackBar = inject(MatSnackBar);
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);

  get effectiveStaffId(): string {
    return this.doctorId || this.loginService.getUser()?.id || '';
  }

  doctorId = '';
  patientId = '';
  reportId = '';
  allProducts?: any[];
  selectedProducts: any[] = [];
  selectedCountry = 'austria';
  report: any;
  isEdit = false;


  acnePercentage = ['0', '25', '50', '75', '100'];
  spfOptions = ['SPF30', 'SPF50'];
  levelOptions = ['No', 'Mild', 'Moderate', 'Severe'];

  isModalVisible = false;
  products?: any[] = [];
  skinIndexList = skin_index;
  reportForm: FormGroup = this.fb.group({
    actinobacteria: [0],
    firmicutes: [0],
    proteobacteria: [0],
    bacteroidetes: [0],
    other: [0],
    cyanobacteria: [0],
    fusobacteriota: [0],
    patescibacteria: [0],
    desulfobacterota: [0],
    spirochaetota: [0],
    synergistota: [0],
    skin_age: [0],
    diversity_index: [0.0],
    relative_abundance: [''],
    skin_index: [''],
    // country: [''],
    acne: [''],
    skin_type: [''],
    wrinkles: [''],
    pigmentation: [''],
    acne_percentage: [this.acnePercentage[2]],
    acne_recommendation: [''],
    skin_recommendation: [''],
    extra_notes: [''],
    spf: [this.spfOptions[0]],
    pigmentation_details: this.fb.group({
      eye_area: [''],
      malar_area: [''],
      overall_face: [''],
      pigmentation_recommendations: [''],
    }),
    wrinkles_details: this.fb.group({
      eye_area: [''],
      nose_area: [''],
      lips_area: [''],
      forehead_area: [''],
      wrinkles_recommendations: [''],
    })
  });

  field1 = [
    {
      name: 'actinobacteria',
      value: 0
    },
    {
      name: 'firmicutes',
      value: 0
    },
    {
      name: 'proteobacteria',
      value: 0
    },
    {
      name: 'bacteroidetes',
      value: 0
    },
    {
      name: 'other',
      value: 0
    }
  ];

  field2 = [
    {
      name: 'cyanobacteria',
      value: 0
    },
    {
      name: 'fusobacteriota',
      value: 0
    },
    {
      name: 'patescibacteria',
      value: 0
    },
    {
      name: 'desulfobacterota',
      value: 0
    },
    {
      name: 'spirochaetota',
      value: 0
    },
    {
      name: 'synergistota',
      value: 0
    }
  ]
  barChartData = barChartData;
  barChartOptions = config.options;

  constructor(
    private location: Location,
    private router: Router,
    private reportService: ReportService,
  ) {

  }

  ngAfterViewInit() {
    this.route.paramMap
      .subscribe(params => {
        this.doctorId = params.get('doctorId') ?? '';
        this.patientId = params.get('patientId') ?? '';
        this.reportId = params.get('reportId') ?? '';
        if (this.reportId) {
          this.reportService.reportsObservable$
            .subscribe((reports: any) => {
              this.report = reports.find((r: any) => r.id === this.reportId);
              if (this.report) {
                this.isEdit = true;
                this.reportForm.patchValue(this.report.data);

                this.productsService.fetchProducts()
                  .subscribe((data: any) => {
                    this.allProducts = data;
                    this.updateProductsList();
                  });
              } else {
                this.reportService.fetchReportDetail(this.effectiveStaffId, this.patientId)
                  .subscribe((reports: any) => {
                    this.reportService.updateReportsList(reports);
                  });
              }
            });
        } else {

          this.productsService.fetchProducts()
            .subscribe((data: any) => {
              this.allProducts = data;
            });
        }
      });
  }


  updateProductsList() {
    this.products = this.allProducts?.filter((p: any) => this.report?.data?.products.includes(p.id));
  }

  get formNumbers() {
    return this.reportForm.value;
  }

  goBack() {
    this.location.back();
  }

  openModal() {
    this.isModalVisible = true;
  }

  closeModal() {
    this.isModalVisible = false;
  }

  selectProduct(product: any) {
    if (this.selectedProducts.includes(product)) {
      this.selectedProducts = this.selectedProducts.filter(p => p !== product);
      return;
    }
    this.selectedProducts.push(product);
  }

  addProducts() {
    this.products = this.selectedProducts;
    this.closeModal();
  }

  gotoProductsPage() {
    this.router.navigate(['/products']);
  }

  submitReport(finalize = false) {
    const staffId = this.effectiveStaffId;
    if (!staffId) {
      this._snackBar.open('Staff user session missing. Please log in again.', 'Close', { duration: 3000 });
      return;
    }
    this.reportService.createReportRequest(staffId, this.patientId).subscribe({
      next: (data: any) => {
        this.createReportForRequest(data.request_id, staffId);
      },
      error: (err: any) => {
        this._snackBar.open('Failed to create report request', 'Close', { duration: 3000 });
      }
    });

  }

  createReportForRequest(requestId: string, staffId?: string) {
    const effectiveStaffId = staffId || this.effectiveStaffId;
    const productIds = this.products?.map(p => p.id);
    const data = {
      request_id: requestId,
      created_by: effectiveStaffId,
      data: {
        ...this.reportForm.value,
        products: productIds
      }
    };
    this.reportService.createReport(data)
      .subscribe({
        next: (data: any) => {
          this.reportService.fetchReportDetail(effectiveStaffId, this.patientId)
            .subscribe((reports: any) => {
              this.reportService.updateReportsList(reports);
              this.location.back();
            });
        },
        error: (err: any) => {
          this._snackBar.open('Failed to create report', 'Close', { duration: 3000 });
        }
      });
  }

  updateReport(finalize = false) {
    const effectiveStaffId = this.effectiveStaffId;
    const productIds = this.products?.map(p => p.id);
    const data = {
      data: {
        ...this.reportForm.value,
        products: productIds,
      },
      finalize: finalize ?? false
    };
    this.reportService.updateReport(this.reportId, data).subscribe({
      next: (data: any) => {
        this.reportService.fetchReportDetail(effectiveStaffId, this.patientId)
          .subscribe((reports: any) => {
            this.reportService.updateReportsList(reports);
            this.location.back();
          });
      },
      error: (err: any) => {
        this._snackBar.open('Failed to update report', 'Close', { duration: 3000 });
      }
    });
    if (this.report?.request_id) {
      this.reportService.markReportRequestCompleted(this.report.request_id).subscribe();
    }
  }

  exportReport(){
    const data = {
      first_name: this.report?.patient?.first_name,
      patient_id: this.patientId,
      doctor_id: this.effectiveStaffId,
      report_id: this.reportId,
      report: this.report
    };

    console.log('data', data);
    this.reportService.exportReport(data).subscribe({
      next: (data: any) => {
      //   data.url download
        console.log('data', data);
        const link = document.createElement('a');
        link.href = data.pdf_url;
        link.download = `report_${this.patientId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this._snackBar.open('Report exported successfully', 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
      }
    });
  }

  protected readonly ReportStatus = ReportStatus;
  protected readonly finalize = finalize;
}
